import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Settings, MoreVertical, Mic, Paperclip, X, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";

export default function Chat() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [activeId, setActiveId] = useState(params.get("c") || null);
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [showPanel, setShowPanel] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: convData } = useQuery({ queryKey: ["conversations"], queryFn: async () => (await api.get("/conversations")).data, refetchInterval: 6000 });
  const conversations = convData?.items || [];

  useEffect(() => {
    if (!activeId && conversations.length) setActiveId(conversations[0].id);
  }, [conversations, activeId]);

  const active = conversations.find((c) => c.id === activeId);

  const { data: msgData } = useQuery({
    queryKey: ["messages", activeId], enabled: !!activeId,
    queryFn: async () => (await api.get(`/conversations/${activeId}/messages`)).data, refetchInterval: 5000,
  });
  const messages = msgData?.items || [];

  const { data: profile } = useQuery({
    queryKey: ["candidate-profile", active?.candidate_id], enabled: !!active?.candidate_id,
    queryFn: async () => (await api.get(`/candidates/${active.candidate_id}/profile`)).data,
  });

  const sendMut = useMutation({
    mutationFn: async (content) => (await api.post(`/conversations/${activeId}/messages`, { content, type: "text" })).data,
    onSuccess: () => { setText(""); qc.invalidateQueries({ queryKey: ["messages", activeId] }); qc.invalidateQueries({ queryKey: ["conversations"] }); },
    onError: (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)),
  });

  const action = async (kind) => {
    setMenuOpen(false);
    try {
      if (kind === "block") { await api.post(`/conversations/${activeId}/block`); toast.success("Usuário bloqueado"); }
      if (kind === "clear") { await api.post(`/conversations/${activeId}/clear-history`); toast.success("Histórico limpo"); }
      if (kind === "delete") { await api.delete(`/conversations/${activeId}`); toast.success("Conversa excluída"); setActiveId(null); }
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["messages", activeId] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const filtered = conversations.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1 mc-container py-8" data-testid="page-chat">
        <div className="text-sm text-muted-foreground"><Link to="/feed" className="hover:text-violet-600">Home</Link> / Chat</div>
        <h1 className="text-3xl font-bold text-[#1e1b2e] mb-5">Chat</h1>

        <div className="mc-card grid grid-cols-1 md:grid-cols-[260px_1fr] lg:grid-cols-[260px_1fr_260px] overflow-hidden min-h-[560px]">
          {/* Lista */}
          <aside className="border-r border-[#f0edf7] p-3" data-testid="conversation-list">
            <div className="relative mb-3">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input className="mc-input !pl-9 py-2 text-sm !bg-[#f7f5fd]" placeholder="Pesquisa" data-testid="chat-search" value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <div className="space-y-1 overflow-y-auto max-h-[480px]">
              {filtered.map((c) => (
                <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => { setActiveId(c.id); setParams({ c: c.id }); }}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors ${c.id === activeId ? "bg-[#f4f2fb]" : "hover:bg-[#f7f5fd]"}`}>
                  <img src={c.photo_url} alt={c.name} className="w-10 h-10 rounded-full object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center gap-2">
                      <span className="font-semibold text-sm text-[#1e1b2e] truncate">{c.name}</span>
                      <span className="text-[11px] text-muted-foreground shrink-0">{fmtTime(c.last_time)}</span>
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-xs text-muted-foreground truncate">{c.last_message || "—"}</span>
                      {c.unread > 0 && <span className="text-[10px] bg-violet-600 text-white rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shrink-0" data-testid={`unread-${c.id}`}>{c.unread}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* Thread */}
          <section className="flex flex-col border-r border-[#f0edf7]" data-testid="chat-thread">
            {active ? (
              <>
                <div className="flex items-center justify-between p-4 border-b border-[#f0edf7]">
                  <div className="flex items-center gap-3">
                    <img src={active.photo_url} alt={active.name} className="w-9 h-9 rounded-full object-cover" />
                    <div>
                      <div className="font-semibold text-sm text-[#1e1b2e]">{active.name}</div>
                      <div className="text-xs text-muted-foreground">{active.online ? "online" : "vista há 5 min"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 relative">
                    <button className="text-muted-foreground p-1" onClick={() => setShowPanel((s) => !s)} data-testid="chat-settings"><Settings size={18} /></button>
                    <button className="text-muted-foreground p-1" onClick={() => setMenuOpen((m) => !m)} data-testid="chat-menu"><MoreVertical size={18} /></button>
                    {menuOpen && (
                      <div className="absolute right-0 top-9 bg-white border border-[#eeebf7] rounded-xl shadow-lg z-10 w-44 py-1" data-testid="chat-actions-menu">
                        <button className="block w-full text-left px-4 py-2 text-sm hover:bg-[#f7f5fd]" data-testid="action-block" onClick={() => action("block")}>Bloquear usuário</button>
                        <button className="block w-full text-left px-4 py-2 text-sm hover:bg-[#f7f5fd]" data-testid="action-clear" onClick={() => action("clear")}>Limpar histórico</button>
                        <button className="block w-full text-left px-4 py-2 text-sm hover:bg-[#f7f5fd] text-destructive" data-testid="action-delete" onClick={() => action("delete")}>Excluir conversa</button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[420px]" data-testid="messages">
                  {messages.map((m, idx) => {
                    const showDate = idx === 0 || messages[idx - 1].date_label !== m.date_label;
                    return (
                      <div key={m.id}>
                        {showDate && m.date_label && <div className="flex justify-center my-3"><span className="mc-grad text-white text-xs px-4 py-1 rounded-full">{m.date_label}</span></div>}
                        <div className={`flex ${m.sender === "me" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.sender}`}>
                          <div className="max-w-[75%]">
                            {m.sender === "them" && <div className="text-xs font-semibold text-violet-600 mb-1">{active.name}</div>}
                            <div className={`px-4 py-2.5 rounded-2xl text-sm ${m.sender === "me" ? "bg-[#ece8fb] text-[#1e1b2e] rounded-br-sm" : "bg-[#f3f2f6] text-[#1e1b2e] rounded-bl-sm"}`}>{m.content}</div>
                            <div className={`text-[11px] text-muted-foreground mt-1 ${m.sender === "me" ? "text-right" : ""}`}>{m.time_label}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <form className="p-3 border-t border-[#f0edf7] flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) sendMut.mutate(text.trim()); }}>
                  <input className="mc-input py-2.5 !bg-[#f7f5fd]" placeholder="Responder" data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} disabled={active.status === "blocked"} />
                  <button type="button" className="text-muted-foreground p-2" data-testid="chat-mic"><Mic size={18} /></button>
                  <button type="button" className="text-muted-foreground p-2" data-testid="chat-attach"><Paperclip size={18} /></button>
                  <button type="submit" className="btn-primary !px-4 !py-2.5" data-testid="chat-send"><Send size={16} /></button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 px-8" data-testid="chat-empty">
                <span className="w-14 h-14 rounded-full mc-grad text-white flex items-center justify-center">
                  <MessageCircle size={26} />
                </span>
                <p className="text-sm text-muted-foreground max-w-[240px]">
                  Escolha uma conversa ao lado para começar a falar com seus matches.
                </p>
              </div>
            )}
          </section>

          {/* Painel de perfil */}
          {showPanel && active && (
            <aside className="p-4 hidden lg:block" data-testid="profile-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <img src={active.photo_url} alt={active.name} className="w-9 h-9 rounded-full object-cover" />
                  <span className="font-semibold text-sm text-[#1e1b2e]">{active.name}</span>
                </div>
                <button className="text-muted-foreground" onClick={() => setShowPanel(false)} data-testid="panel-close"><X size={18} /></button>
              </div>
              <PanelRow label="Nome perfil" value={active.name} />
              <PanelRow label="" value={profile?.handle} />
              <PanelRow label="Bio" value={profile?.bio} />
              <div className="mt-4">
                <div className="text-sm font-semibold text-[#1e1b2e] mb-2">Documentos Verificados</div>
                {(profile?.verifications || []).length ? profile.verifications.map((v, i) => (
                  <div key={i} className="text-sm text-violet-600 font-medium py-0.5" data-testid={`verif-${v.type}`}>
                    {v.type === "CERTIDAO_CIVIL" ? "CERTIDÃO CIVIL" : v.type} {v.status === "verified" ? "✓" : "…"}
                  </div>
                )) : <div className="text-xs text-muted-foreground">Disponível apenas após match.</div>}
              </div>
              <div className="mt-4">
                <div className="text-sm font-semibold text-[#1e1b2e] mb-2">Notificações</div>
                <button className="block text-sm text-violet-600 py-0.5" data-testid="panel-block" onClick={() => action("block")}>Bloquear usuário</button>
                <button className="block text-sm text-violet-600 py-0.5" data-testid="panel-clear" onClick={() => action("clear")}>Limpar histórico</button>
                <button className="block text-sm text-violet-600 py-0.5" data-testid="panel-delete" onClick={() => action("delete")}>Excluir conversa</button>
              </div>
            </aside>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function PanelRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="mb-3">
      {label && <div className="text-sm font-semibold text-[#1e1b2e]">{label}</div>}
      <div className="text-sm text-muted-foreground">{value}</div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    let h = d.getHours(); const m = String(d.getMinutes()).padStart(2, "0");
    const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
    return `${h}:${m} ${ap}`;
  } catch { return ""; }
}
